/**
 * Default template definitions for the Banner Ad Creator.
 *
 * Templates are generated by merging structural "skeleton" definitions
 * (zone types, labels, data bindings, background config) with JSON files
 * in src/templates/builtin/ that use the same format as downloaded project
 * JSON files. This lets you tweak a template in the UI, download the JSON,
 * and drop it into builtin/ to update the defaults.
 *
 * Three built-in templates:
 * 1. Wide — 3D Angled (1600×300)
 * 2. Wide — Forest Blur (1600×300)
 * 3. Rectangle — Forest Blur (1500×750)
 */
import { TEMPLATE_SKELETONS } from './skeletons.js';
import wide3dAngled from './builtin/wide-3d-angled.json';
import wideForestBlur from './builtin/wide-forest-blur.json';
import rectForestBlur from './builtin/rect-forest-blur.json';

const BUILTIN_JSONS = [wide3dAngled, wideForestBlur, rectForestBlur];

/**
 * Build a full template definition by merging a skeleton with a
 * downloaded-format JSON file (templateOverrides + bg settings).
 * @param {object} json - Project save JSON (same format as download)
 * @returns {object} Full template definition
 */
function buildTemplate(json) {
  const id = json.templateId;
  const skel = TEMPLATE_SKELETONS[id];
  if (!skel) throw new Error(`No skeleton for template: ${id}`);

  const overrides = json.templateOverrides || {};
  const canvas = overrides.canvas || {};

  // Merge background processing from JSON sliders into skeleton background
  const background = JSON.parse(JSON.stringify(skel.background));
  if (background && background.type === 'image') {
    if (json.bgBlur !== undefined) background.processing = background.processing || {};
    if (json.bgBlur !== undefined) background.processing.blur = json.bgBlur;
    if (json.bgBrightness !== undefined) {
      background.processing.brightness = -(json.bgBrightness / 100);
    }
  }

  // Build zones: skeleton provides structural props, JSON provides bounds/style/transform
  const zoneOverrides = overrides.zones || [];
  const zones = zoneOverrides.map(zo => {
    const skelZone = skel.zones[zo.id];
    if (!skelZone) throw new Error(`No skeleton for zone: ${zo.id} in template: ${id}`);
    return {
      id: zo.id,
      type: skelZone.type,
      label: skelZone.label,
      bounds: { ...zo.bounds },
      transform: zo.transform ? { ...zo.transform } : undefined,
      style: zo.style ? { ...zo.style } : undefined,
      binding: skelZone.binding,
      ...(skelZone.icons ? { icons: [...skelZone.icons] } : {}),
    };
  });

  return {
    id,
    name: skel.name,
    version: json.version || 1,
    magazine: { ...skel.magazine },
    canvas: { ...canvas },
    background,
    zones,
    variables: json.variables || {},
  };
}

export const DEFAULT_TEMPLATES = BUILTIN_JSONS.map(buildTemplate);

/**
 * Get a template by ID.
 * @param {string} id
 * @returns {object|undefined}
 */
export function getTemplateById(id) {
  return DEFAULT_TEMPLATES.find(t => t.id === id);
}

/**
 * Get all templates that match a canvas size.
 * @param {number} width
 * @param {number} height
 * @returns {object[]}
 */
export function getTemplatesBySize(width, height) {
  return DEFAULT_TEMPLATES.filter(
    t => t.canvas.width === width && t.canvas.height === height
  );
}

/**
 * Get all template IDs and names for UI selection.
 * @returns {{ id: string, name: string, width: number, height: number }[]}
 */
export function getTemplateList() {
  return DEFAULT_TEMPLATES.map(t => ({
    id: t.id,
    name: t.name,
    width: t.canvas.width,
    height: t.canvas.height,
  }));
}
